import React, { useEffect, useMemo } from 'react'
import { ReservationForm, SpaceDetail, formatTimeWith24, getStartAvailabilityTimes, timeToMinutes } from '@/features/space/spaces-slice'
import dayjs from 'dayjs'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import { Control, Controller, UseFormSetValue } from 'react-hook-form'
import { SelectBox } from '@/components/atoms/select-box'
import { css } from '@emotion/react'
dayjs.extend(isSameOrAfter)

type Props = {
  space: SpaceDetail
  control: Control<ReservationForm, any>
  setValue: UseFormSetValue<ReservationForm>
  watchValues: {
    quantity: number
    useDay: string
    usageFromHour: string
    usageFromMinute: string
    usageToHour: string
    usageToMinute: string
  }
}

export const ReserveTimeBox: React.FC<Props> = ({ space, control, setValue, watchValues }) => {
  const makeHourOptions = (times: string[]): { label: string; value: string }[] => {
    if (!space) {
      return []
    }
    const pickedValue: string[] = []
    return times
      .map(time => ({
        label: time.split(':')[0],
        value: time.split(':')[0],
      }))
      .filter(opt => {
        if (pickedValue.includes(opt.value)) {
          return false
        }
        pickedValue.push(opt.value)
        return true
      })
  }

  const makeSelectMinutesOption = (times: string[], hour: string) => {
    return (times || [])
      .filter(d => d.split(':')[0] === hour)
      .map(d => {
        const item = d.split(':')[1]
        return { label: item, value: item }
      })
  }

  const findNearestTime = (times: string[], selectedTime: string) => {
    const selectedMinutes = timeToMinutes(selectedTime, space.usageIntervalTime)

    return times.reduce((nearest, current) => {
      const currentMinutes = timeToMinutes(current, space.usageIntervalTime)
      const nearestMinutes = timeToMinutes(nearest, space.usageIntervalTime)

      return Math.abs(currentMinutes - selectedMinutes) < Math.abs(nearestMinutes - selectedMinutes) ? current : nearest
    })
  }

  const minUsageMinute = useMemo(() => timeToMinutes(space.spacePrice?.chargePrice.usageTime.start, space.usageIntervalTime), [space])

  const startAvailabilityTimes = useMemo((): string[] => {
    if (!space) {
      return []
    }
    return getStartAvailabilityTimes(space, watchValues.quantity)
  }, [space?.availabilityTimes, watchValues.quantity])

  const endAvailabilityTimes = useMemo((): string[] => {
    if (!space) {
      return []
    }
    let firsStartTime: string
    firsStartTime = Object.keys(space.availabilityTimes || {})[0]
    if (watchValues.usageFromHour && watchValues.usageFromMinute) {
      firsStartTime = `${watchValues.usageFromHour}:${watchValues.usageFromMinute}`
    }
    const firstStartTimeAtDayjs = dayjs(watchValues.useDay)
      .set('hour', Number(firsStartTime.split(':')[0]))
      .set('minute', Number(firsStartTime.split(':')[1]))

    // 終了は在庫時間を15分ずらしたものを使用する
    // e.g.)10:45の在庫を11:00に適用
    let mappedTimes = Object.keys(space.availabilityTimes).map(time => ({
      time: dayjs(`${watchValues.useDay} ${time}`).add(15, 'minute').format('HH:mm'),
      stock: space.availabilityTimes[time],
    }))

    // 最低利用時間以降のストックを参照
    const minUsageTime = firstStartTimeAtDayjs.add(minUsageMinute, 'minute').format('HH:mm')
    const minUsageTimeIndex = mappedTimes.findIndex(({ time }) => {
      const currentTimeDayJs = dayjs(`${watchValues.useDay} ${formatTimeWith24(time)}`)
      const minUsageTimeDayJs = dayjs(`${watchValues.useDay} ${formatTimeWith24(minUsageTime)}`)
      return currentTimeDayJs.isSameOrAfter(minUsageTimeDayJs)
    })
    if (minUsageTimeIndex === -1) {
      return []
    }
    mappedTimes = mappedTimes.slice(minUsageTimeIndex)

    // 最大利用時間をもとに終了時間のストックを参照
    if (space.spacePrice?.chargePrice.usageTime.end) {
      const maxUsageMinute = timeToMinutes(space.spacePrice.chargePrice.usageTime.end, space.usageIntervalTime)
      const maxUsageTimeDayJs = firstStartTimeAtDayjs.add(maxUsageMinute, 'minute')

      const maxUsageTimeIndex = mappedTimes.findIndex(({ time }) => {
        const currentTimeDayJs = dayjs(`${watchValues.useDay} ${time}`)
        return currentTimeDayJs.isAfter(maxUsageTimeDayJs)
      })
      if (maxUsageTimeIndex !== -1) {
        mappedTimes = mappedTimes.slice(0, maxUsageTimeIndex)
      }
    }

    // 連続して在庫を確保できる枠にスライス
    const overstockTimeIndex = mappedTimes.findIndex(({ stock }) => stock < watchValues.quantity)
    if (overstockTimeIndex !== -1) {
      mappedTimes = mappedTimes.slice(0, overstockTimeIndex)
    }
    return mappedTimes.map(({ time }) => time)
  }, [startAvailabilityTimes, watchValues.usageFromHour, watchValues.usageFromMinute, watchValues.quantity])

  useEffect(() => {
    if (!startAvailabilityTimes[0]) {
      return
    }
    if (!watchValues.usageFromHour) {
      setValue('usageFromHour', '00', { shouldValidate: true })
      setValue('usageFromMinute', '00', { shouldValidate: true })
      return
    }
    if (!startAvailabilityTimes.includes(`${watchValues.usageFromHour}:${watchValues.usageFromMinute}`)) {
      const changedHourIndex = startAvailabilityTimes.findIndex(time => time.includes(`${watchValues.usageFromHour}:`))
      if (changedHourIndex !== -1) {
        setValue('usageFromHour', startAvailabilityTimes[changedHourIndex].split(':')[0], { shouldValidate: true })
        setValue('usageFromMinute', startAvailabilityTimes[changedHourIndex].split(':')[1], { shouldValidate: true })
      } else {
        setValue('usageFromHour', startAvailabilityTimes[0].split(':')[0], { shouldValidate: true })
        setValue('usageFromMinute', startAvailabilityTimes[0].split(':')[1], { shouldValidate: true })
      }
    }
  }, [startAvailabilityTimes, watchValues.usageFromHour])

  useEffect(() => {
    if (!endAvailabilityTimes[0]) {
      setValue('usageToHour', '', { shouldValidate: true })
      setValue('usageToMinute', '', { shouldValidate: true })
      return
    }
    if (!watchValues.usageToHour || !watchValues.usageToMinute) {
      setValue('usageToHour', watchValues.usageFromHour, { shouldValidate: true })
      setValue('usageToMinute', watchValues.usageFromMinute, { shouldValidate: true })
      return
    }
    const selectedTime = `${watchValues.usageToHour}:${watchValues.usageToMinute}`
    if (!endAvailabilityTimes.includes(selectedTime)) {
      if (Math.max(...endAvailabilityTimes.map(t => Number(t.split(':')[0]))) <= Number(watchValues.usageToHour)) {
        setValue('usageToHour', endAvailabilityTimes[endAvailabilityTimes.length - 1].split(':')[0], { shouldValidate: true })
        setValue('usageToMinute', endAvailabilityTimes[endAvailabilityTimes.length - 1].split(':')[1], { shouldValidate: true })
      } else {
        // 選択された時間から最も近い時間を選択
        const nearestTime = findNearestTime(endAvailabilityTimes, selectedTime)
        setValue('usageToHour', nearestTime.split(':')[0], { shouldValidate: true })
        setValue('usageToMinute', nearestTime.split(':')[1], { shouldValidate: true })
      }
    }
  }, [endAvailabilityTimes, watchValues.usageToHour, watchValues.usageToMinute])

  return (
    <>
      <Controller
        control={control}
        name="usageFromHour"
        rules={{ required: true }}
        render={({ field }) => (
          <SelectBox {...field} itemCss={selectStyle} options={makeHourOptions(startAvailabilityTimes)} arrowDefaultSelect={false} />
        )}
      />
      :
      <Controller
        control={control}
        name="usageFromMinute"
        rules={{ required: true }}
        render={({ field }) => (
          <SelectBox
            {...field}
            itemCss={selectStyle}
            options={makeSelectMinutesOption(startAvailabilityTimes, watchValues.usageFromHour)}
            arrowDefaultSelect={false}
          />
        )}
      />
      {!space.isStockByStartTime && (
        <>
          ~
          <Controller
            control={control}
            name="usageToHour"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectBox
                {...field}
                itemCss={selectStyle}
                disabled={space.isFixAvailabilityTime || makeHourOptions(endAvailabilityTimes).length === 0}
                options={makeHourOptions(endAvailabilityTimes)}
                arrowDefaultSelect={false}
              />
            )}
          />
          :
          <Controller
            control={control}
            name="usageToMinute"
            rules={{ required: true }}
            render={({ field }) => (
              <SelectBox
                {...field}
                itemCss={selectStyle}
                disabled={space.isFixAvailabilityTime || makeHourOptions(endAvailabilityTimes).length === 0}
                options={makeSelectMinutesOption(endAvailabilityTimes, watchValues.usageToHour)}
                arrowDefaultSelect={false}
              />
            )}
          />
        </>
      )}
    </>
  )
}
const selectStyle = css({
  '.select-wrapper': { marginTop: 0 },
  select: { padding: '10px 16px' },
})
